import { RegionIcon } from "./RegionViewBottom";

const ShotItem = ({ shot, index }) => {
	const isMade = shot.field_goal_result === "score";
	const shotType =
		shot.field_goal_position === "3PFG"
			? "Three pointer"
			: shot.field_goal_position === "FT"
				? "Free throw"
				: shot.shot_type || "Swish";
	const shotName =
		shot.shot_name ||
		(shot.field_goal_position === "FT" ? "Free throw" : "Layup");

	// Convert timestamp to MM:SS format
	const formatTimestamp = (timestamp) => {
		if (!timestamp) return `00:${String(index).padStart(2, "0")}`;

		// If timestamp is a large unix timestamp (in seconds), convert to game time
		// Assume it's seconds within a game (0-48 minutes for basketball)
		const totalSeconds =
			typeof timestamp === "number" && timestamp > 10000
				? Math.floor(timestamp % 3600) // Get seconds within the hour
				: Math.floor(timestamp);

		const minutes = Math.floor(totalSeconds / 60);
		const seconds = totalSeconds % 60;
		return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
	};

	return (
		<div className="bg-white flex items-center gap-3 p-2 rounded">
			{/* Made/Missed Label */}
			<div className="w-[60px] shrink-0">
				<div
					className={`px-2 py-1 rounded text-xs font-medium text-center ${
						isMade
							? "bg-color-Green50 text-color-Green500"
							: "bg-color-Grey50 text-color-Grey700"
					}`}
				>
					{isMade ? "Made" : "Missed"}
				</div>
			</div>

			{/* Shot Info */}
			<div className="flex-1 flex flex-col justify-center min-w-0">
				<div className="text-xs text-color-Grey700 tracking-[0.25px] leading-[1.25]">
					{shotType}
				</div>
				<div className="text-sm font-medium text-color-Grey900 leading-[1.35] overflow-hidden text-ellipsis whitespace-nowrap">
					{shotName}
				</div>
			</div>

			{/* Timestamp */}
			<div className="w-[80px] shrink-0 text-xs text-color-Grey300 text-right tracking-[0.25px] leading-[1.25]">
				{formatTimestamp(shot.timestamp)}
			</div>
		</div>
	);
};

export const RegionDetailView = ({ region, onClose }) => {
	if (!region) return null;

	// Get shots specific to this region and sort by timestamp
	const regionShotsData = (region.shots || []).sort((a, b) => {
		// Handle cases where timestamp might be missing
		const timestampA = a.timestamp || 0;
		const timestampB = b.timestamp || 0;

		// Convert large timestamps to game time for comparison
		const gameTimeA =
			typeof timestampA === "number" && timestampA > 10000
				? timestampA % 3600
				: timestampA;
		const gameTimeB =
			typeof timestampB === "number" && timestampB > 10000
				? timestampB % 3600
				: timestampB;

		return gameTimeA - gameTimeB;
	});

	return (
		<div className="w-full h-full flex flex-col gap-3 bg-white p-3 rounded-lg">
			{/* Back button - outside the main card */}
			<button
				onClick={onClose}
				className="bg-color-Grey50 hover:bg-gray-200 transition-colors flex items-center gap-1 px-2 py-1 rounded text-sm font-medium text-color-Grey700 self-start shadow-sm"
			>
				<svg
					width="16"
					height="16"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={2}
						d="M10 19l-7-7m0 0l7-7m-7 7h18"
					/>
				</svg>
				Back to ranking
			</button>

			{/* Main card container */}
			<div className="flex-1 flex flex-col bg-color-Grey50 border border-color-Grey100 rounded-lg p-3 gap-3">
				{/* Region info */}
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-2">
						<div className="scale-125">
							<RegionIcon regionName={region.name} />
						</div>
						<h2 className="text-xl font-bold text-color-Grey900 italic">
							{region.name}
						</h2>
					</div>
					<div className="text-2xl font-bold text-color-Grey900">
						{region.percentage}%
					</div>
				</div>

				{/* Progress bar */}
				<div className="relative h-5 bg-color-Grey100 rounded overflow-hidden">
					<div
						className="absolute -left-[0.005rem] -top-[0.005rem] h-full bg-Gradient transition-all duration-300"
						style={{ width: `${region.percentage}%` }}
					/>
					<div className="absolute inset-0 flex items-center justify-center text-xs font-medium text-white">
						{region.made}/{region.total}
					</div>
				</div>

				{/* Shot list */}
				<div className="flex-1 overflow-y-auto max-h-[200px]">
					{regionShotsData.length > 0 ? (
						<div className="flex flex-col gap-3">
							{regionShotsData.map((shot, index) => (
								<ShotItem key={index} shot={shot} index={index} />
							))}
						</div>
					) : (
						<div className="text-center py-8 text-color-Grey500">
							<p className="text-sm">No shots in this region</p>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};
